import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Phone, MapPin, ChevronDown } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = '' }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: '110%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const contentY = useTransform(scrollY, [0, 700], [0, 140]);
  const contentOpacity = useTransform(scrollY, [0, 550], [1, 0]);

  return (
    <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Signature intro: brand curtain wipe */}
      <motion.div
        className="fixed inset-0 z-[70] bg-black flex items-center justify-center pointer-events-none"
        initial={{ clipPath: 'inset(0% 0% 0% 0%)' }}
        animate={{ clipPath: 'inset(0% 0% 100% 0%)' }}
        transition={{ duration: 1, delay: 0.55, ease: [0.76, 0, 0.24, 1] }}
        data-testid="intro-curtain"
        aria-hidden="true"
      >
        <motion.span
          className="font-display italic text-yellow-500 text-2xl md:text-4xl tracking-wide"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1, ease: EASE }}
        >
          İskenderun Konaklama
        </motion.span>
      </motion.div>

      {/* Parallax background */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <motion.img
          src="https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/g6lv6pwz_Resepsiyon.jpeg"
          alt="İskenderun Konaklama Resepsiyon"
          className="w-full h-full object-cover opacity-40"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, delay: 0.35, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
        <div className="absolute inset-0 bg-black/30"></div>
      </motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 container mx-auto px-4 text-center mt-20"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <motion.a
          href="https://maps.app.goo.gl/dSLK2f9mchtDUE167"
          target="_blank"
          rel="noreferrer"
          data-testid="hero-location-badge"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-sm mb-8 hover:bg-yellow-500 hover:text-black transition-colors group cursor-pointer"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
        >
          <MapPin size={16} className="text-yellow-500 group-hover:text-black transition-colors" />
          <span className="text-yellow-500 text-sm font-medium tracking-wider uppercase group-hover:text-black transition-colors">İskenderun, Hatay</span>
        </motion.a>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6 leading-[1.15] tracking-tight" data-testid="hero-title">
          <MaskedLine delay={1.1}>Konforun ve Şıklığın</MaskedLine>
          <MaskedLine delay={1.25} className="italic text-yellow-500">Yeni Adresi</MaskedLine>
        </h1>

        <motion.p
          className="text-base md:text-lg text-zinc-300 mb-10 max-w-2xl mx-auto font-light leading-relaxed"
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
        >
          İskenderun merkezde, evinizin rahatlığını aratmayacak modern, lüks ve hijyenik dairelerimizde sizi misafir etmekten mutluluk duyarız.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.65, ease: EASE }}
        >
          <motion.a
            href="tel:+905416410171"
            data-testid="hero-cta-call"
            whileHover={{ y: -4 }}
            className="w-full sm:w-auto flex items-center justify-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 rounded-full font-bold text-lg transition-[background-color,box-shadow] duration-300 hover:shadow-[0_10px_40px_-10px_rgba(234,179,8,0.5)]"
          >
            <Phone size={22} />
            <span>Rezervasyon Yap</span>
          </motion.a>
          <motion.a
            href="#gallery"
            data-testid="hero-cta-gallery"
            whileHover={{ y: -4 }}
            className="w-full sm:w-auto flex items-center justify-center gap-3 bg-transparent border-2 border-zinc-500 hover:border-yellow-500 hover:text-yellow-500 text-white px-8 py-4 rounded-full font-bold text-lg transition-colors duration-300"
          >
            Daireleri İncele
          </motion.a>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.a
        href="#gallery"
        data-testid="hero-scroll-cue"
        aria-label="Aşağı kaydır"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-zinc-500 hover:text-yellow-500 transition-colors"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
          <ChevronDown size={28} />
        </motion.div>
      </motion.a>
    </section>
  );
};

export default Hero;
